/**
 * Pick handling for the group-compose template selector. The control keeps showing the template it
 * inserted — one that snaps back to its placeholder reads as "no template used", which is the whole
 * complaint — and a pick replaces the message body rather than appending to it, so the box always
 * holds exactly the named template plus whatever was edited after.
 */

interface TemplateBody {
  body_type?: string | null
  message_body?: string | null
}

type Template = {
  id?: string | null
  message_bodies?: TemplateBody[] | null
  [key: string]: unknown
}

interface TemplateEntry {
  value?: Template | null
  text?: string | null
}

/** A ModelSelect option: it binds the whole object and searches on `text`. */
export interface TemplateChoice {
  value: string
  text: string
}

export interface TemplatePick {
  choice: TemplateChoice
  template: TemplateEntry['value'] | null
}

export const NO_TEMPLATE_CHOICE: TemplateChoice = { value: '', text: '' }

/** The picker's options — id identifies the pick, name is what merchants read. */
export function templateChoices(templates: TemplateEntry[]): TemplateChoice[] {
  return templates.map(template => ({
    value: template.value?.id ?? '',
    text: template.text ?? ''
  }))
}

/**
 * Resolve an emitted option against the loaded templates. An option with no id
 * (backspace clears the control to `{}`) or an id the list no longer carries
 * resolves to a cleared control: a name with no template behind it would claim
 * text that never got inserted.
 */
export function resolveTemplatePick(
  templates: TemplateEntry[],
  option: TemplateChoice | Record<string, unknown> | null | undefined
): TemplatePick {
  const id = (option as TemplateChoice | null)?.value

  if (!id) return { choice: NO_TEMPLATE_CHOICE, template: null }

  const selected = templates.find(template => template.value?.id === id)

  if (!selected) return { choice: NO_TEMPLATE_CHOICE, template: null }

  return {
    // Label from the template rather than from the option, so the control can
    // never show a name the list has since changed.
    choice: { value: id, text: selected.text ?? '' },
    template: selected.value ?? null
  }
}

/**
 * The text a group message sends. Templates carry one body per channel and
 * group compose posts a single `message`, so TEXT is the body it means; an
 * email-only template has nothing to load and yields an empty box rather than
 * reading `message_body` off undefined.
 */
export function templateMessageText(template: Template | null | undefined) {
  const body = template?.message_bodies?.find(body => body.body_type === 'TEXT')

  return body?.message_body ?? ''
}
